import React from 'react';
import styled from 'styled-components';
import { Colors } from 'modules/theme';
import PropTypes from 'prop-types';

const FILTER_INPUT_HEIGHT = 44;
const FILTER_INPUT_RADIUS = 6;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
`;

const Label = styled.label`
  display: block;
  color: ${Colors.gray8};
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  margin-bottom: 6px;
  white-space: nowrap;
  @media (max-width: 767px) {
    white-space: normal;
    margin-bottom: 8px;
  }
`;

const Select = styled.select`
  width: 100%;
  min-width: 0;
  height: ${FILTER_INPUT_HEIGHT}px;
  padding: 0 36px 0 12px;
  box-sizing: border-box;
  background-color: ${Colors.white};
  border: 1px solid #ecebf0;
  border-radius: ${FILTER_INPUT_RADIUS}px;
  font-size: 15px;
  line-height: 20px;
  color: ${Colors.gray};
  outline: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  cursor: pointer;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23314683' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;

  ::placeholder {
    color: ${Colors.gray};
  }
  option {
    color: ${Colors.blue};
    font-weight: 400;
  }
  &:focus {
    border-color: ${Colors.lightBlue};
  }
`;

const CompactSelect = styled(Select)`
  width: auto;
  height: 40px;
  min-width: 132px;
  max-width: 196px;
  padding: 0 32px 0 14px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  text-overflow: ellipsis;
  color: ${props => (props.$active ? Colors.black2 : Colors.gray)};
  border-color: ${props => (props.$active ? Colors.lightBlue : Colors.gray2)};
  background-color: ${props => (props.$active ? 'rgba(20, 152, 203, 0.08)' : Colors.white)};
  transition: border-color 120ms ease, background-color 120ms ease;
`;

export const FilterGroup = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 20px 24px;
  padding: 20px;
  margin-top: 0;
  margin-bottom: 20px;
  background-color: ${Colors.white};
  border: 1px solid #ecebf0;
  border-radius: 12px;
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.05);
  @media (max-width: 767px) {
    padding: 16px;
    gap: 16px;
    margin-top: 0;
    margin-bottom: 16px;
    border-radius: 12px;
  }
`;

export const FilterContainer = styled.div`
  flex: 1 1 160px;
  min-width: 140px;
  max-width: 240px;
  box-sizing: border-box;
  @media (max-width: 767px) {
    min-width: 0;
    flex: 1 1 100%;
    max-width: 100%;
  }
`;

const FilterComponent = props => {
  const { title, name, selectedValue, handleChange, options, compact } = props;
  const id = `filter-${name}`;

  if (compact) {
    const isActive = selectedValue !== '' && selectedValue !== null && selectedValue !== undefined;
    return (
      <CompactSelect
        id={id}
        name={name}
        value={selectedValue}
        onChange={handleChange}
        aria-label={title}
        $active={isActive}
      >
        {options.map(({ value, label }, key) => (
          <option value={value} key={key}>
            {label}
          </option>
        ))}
      </CompactSelect>
    );
  }

  return (
    <Container>
      <Label htmlFor={id}>{title}</Label>
      <Select id={id} name={name} value={selectedValue} onChange={handleChange}>
        {options.map(({ value, label }, key) => (
          <option value={value} key={key}>
            {label}
          </option>
        ))}
      </Select>
    </Container>
  );
};

FilterComponent.propTypes = {
  compact: PropTypes.bool,
  handleChange: PropTypes.func,
  name: PropTypes.string,
  options: PropTypes.array,
  selectedValue: PropTypes.string,
  title: PropTypes.string,
};

export default FilterComponent;
